import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { ShieldCheck, Zap, MessageCircle, ArrowRight, CreditCard, Star, Quote } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { waLink, BNPL, BRANDS } from "@/lib/constants";

const HERO_BG =
  "https://images.unsplash.com/photo-1726835498689-b4f6dbcdbdfb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const ADVANTAGES = [
  { icon: ShieldCheck, title: "Garansi Resmi", desc: "Semua produk bergaransi resmi Indonesia, bukan inter/refurbish." },
  { icon: Zap, title: "100% Brand New", desc: "Segel utuh, unit baru langsung dari distributor terpercaya." },
  { icon: CreditCard, title: "Bisa Cicilan", desc: "Kredivo, Akulaku, Indodana, hingga kartu kredit. Ringan di kantong." },
  { icon: MessageCircle, title: "Respons Cepat", desc: "Tim kami siap membantu & closing cepat via WhatsApp." },
];

const TESTIMONIALS = [
  { name: "Rangga P.", text: "Order MacBook, prosesnya cepet banget dan barang ori segel. Bisa cicilan lagi, mantap!", brand: "Apple" },
  { name: "Sisca W.", text: "CS responsif banget di WA, dikasih penjelasan detail sebelum beli. Garansi resmi aman.", brand: "Samsung" },
  { name: "Dimas A.", text: "Harga kompetitif, unit Pixel-nya baru dan bergaransi. Recommended seller!", brand: "Google" },
];

function Section({ children, className = "" }) {
  return <section className={`mx-auto max-w-7xl px-4 sm:px-6 ${className}`}>{children}</section>;
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8)));
    api.get("/products", { params: { bestseller: true } }).then((r) => setBestsellers(r.data.slice(0, 8)));
  }, []);

  return (
    <div>
      <Seo />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grain absolute inset-0" />
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/40 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:py-40">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-gray-200 backdrop-blur">
              <Star className="h-3.5 w-3.5 text-berry" /> Premium Gadget Reseller
            </span>
            <h1 className="mt-6 font-heading text-4xl font-800 leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Gadget Premium, <span className="text-berry">Bergaransi Resmi</span> & Brand New.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-gray-300 md:text-lg">
              Apple, Samsung, Google, GoPro, JBL, Bose & lainnya. Semua original, bergaransi resmi
              Indonesia, dan bisa dicicil. Closing mudah langsung via WhatsApp.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp"
                className="inline-flex items-center gap-2 rounded-full bg-whatsapp px-6 py-3.5 font-semibold text-white shadow-[0_8px_30px_rgb(0,0,0,0.5)] transition-all duration-300 hover:scale-105 hover:bg-whatsapp-dark"
              >
                <FaWhatsapp className="h-5 w-5" /> Konsultasi via WhatsApp
              </a>
              <Link
                to="/katalog"
                data-testid="hero-catalog"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3.5 font-semibold text-white backdrop-blur transition-colors hover:bg-white/10"
              >
                Lihat Katalog <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BRAND STRIP */}
      <Section className="-mt-8 relative z-10 pb-4">
        <div className="flex flex-wrap items-center justify-center gap-2.5 rounded-2xl border border-white/5 bg-[#12141C] p-5">
          {BRANDS.map((b) => (
            <Link
              key={b}
              to={`/brand/${encodeURIComponent(b)}`}
              className="rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-gray-300 transition-all hover:-translate-y-0.5 hover:border-berry hover:text-white"
            >
              {b}
            </Link>
          ))}
        </div>
      </Section>

      {/* ADVANTAGES */}
      <Section className="py-16">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {ADVANTAGES.map((a) => (
            <div key={a.title} className="rounded-2xl border border-white/5 bg-[#12141C] p-6 transition-colors hover:border-white/20">
              <div className="mb-4 inline-flex rounded-xl bg-berry/10 p-3 text-berry">
                <a.icon className="h-6 w-6" />
              </div>
              <h3 className="font-heading text-lg font-600 text-white">{a.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-400">{a.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <Section className="py-6">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <p className="text-sm font-medium uppercase tracking-widest text-berry">Pilihan Unggulan</p>
              <h2 className="mt-1 font-heading text-3xl font-700 tracking-tight text-white">Produk Terbaru & Unggulan</h2>
            </div>
            <Link to="/katalog" className="hidden items-center gap-1 text-sm text-gray-300 hover:text-white sm:inline-flex">
              Semua produk <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </Section>
      )}

      {/* BNPL / CICILAN BENTO */}
      <Section className="py-16">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="relative overflow-hidden rounded-3xl border border-electric/30 bg-gradient-to-br from-electric/20 to-[#12141C] p-8 md:col-span-2 md:p-12">
            <span className="inline-flex items-center gap-2 rounded-full bg-electric/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-300">
              <CreditCard className="h-4 w-4" /> Cicilan & BNPL
            </span>
            <h2 className="mt-5 font-heading text-3xl font-700 tracking-tight text-white md:text-4xl">
              Punya gadget impian tanpa harus tunai.
            </h2>
            <p className="mt-3 max-w-lg text-gray-300">
              Cicil ringan lewat partner pembayaran favorit. Cukup konsultasi via WhatsApp, tim kami
              bantu proses cicilan Anda dari awal sampai barang di tangan.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {BNPL.map((b) => (
                <span key={b} className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white">
                  {b}
                </span>
              ))}
            </div>
            <Link to="/cara-pembelian" className="mt-7 inline-flex items-center gap-2 rounded-full bg-electric px-5 py-3 font-semibold text-white transition-colors hover:bg-electric-dark">
              Cara Beli & Cicilan <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="flex flex-col justify-between rounded-3xl border border-white/5 bg-[#12141C] p-8">
            <div>
              <h3 className="font-heading text-2xl font-700 text-white">Kenapa BigBerry?</h3>
              <ul className="mt-5 space-y-3 text-sm text-gray-300">
                <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-berry" /> Garansi resmi Indonesia</li>
                <li className="flex items-center gap-2"><Zap className="h-4 w-4 text-berry" /> Unit 100% brand new segel</li>
                <li className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-berry" /> Layanan cepat & ramah</li>
                <li className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-berry" /> Banyak opsi pembayaran</li>
              </ul>
            </div>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-5 py-3 font-semibold text-white transition-colors hover:bg-whatsapp-dark">
              <FaWhatsapp className="h-4 w-4" /> Chat Sekarang
            </a>
          </div>
        </div>
      </Section>

      {/* BEST SELLER */}
      {bestsellers.length > 0 && (
        <Section className="py-6">
          <div className="mb-8">
            <p className="text-sm font-medium uppercase tracking-widest text-berry">Paling Dicari</p>
            <h2 className="mt-1 font-heading text-3xl font-700 tracking-tight text-white">Best Seller</h2>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {bestsellers.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </Section>
      )}

      {/* TESTIMONIALS */}
      <Section className="py-16">
        <div className="mb-8 text-center">
          <p className="text-sm font-medium uppercase tracking-widest text-berry">Kata Mereka</p>
          <h2 className="mt-1 font-heading text-3xl font-700 tracking-tight text-white">Dipercaya Pelanggan</h2>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="rounded-2xl border border-white/5 bg-[#12141C] p-6">
              <Quote className="h-7 w-7 text-berry/70" />
              <p className="mt-4 text-sm leading-relaxed text-gray-200">"{t.text}"</p>
              <div className="mt-5 flex items-center gap-1 text-berry">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-berry" />
                ))}
              </div>
              <p className="mt-3 text-sm font-semibold text-white">{t.name} <span className="font-normal text-gray-500">· {t.brand}</span></p>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section className="pb-20">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-berry/25 via-[#12141C] to-[#12141C] p-10 text-center md:p-16">
          <h2 className="font-heading text-3xl font-700 tracking-tight text-white md:text-4xl">
            Siap upgrade gadget Anda?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-gray-300">
            Konsultasikan kebutuhan Anda sekarang. Tim BigBerry siap bantu pilih produk terbaik + opsi cicilan.
          </p>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center gap-2 rounded-full bg-whatsapp px-7 py-3.5 font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-whatsapp-dark">
            <FaWhatsapp className="h-5 w-5" /> Hubungi via WhatsApp
          </a>
        </div>
      </Section>
    </div>
  );
}
